import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  BookOpen,
  Search,
  ClipboardList,
  Stethoscope,
  Users,
  Pill,
  Receipt,
  Printer,
  ShieldCheck,
  Building2,
  HelpCircle,
  ArrowRight,
  CheckCircle2,
} from "lucide-react";

const categories = [
  { id: "getting-started", label: "Getting Started", icon: BookOpen },
  { id: "reception", label: "Reception", icon: ClipboardList },
  { id: "doctor", label: "Doctor Workflow", icon: Stethoscope },
  { id: "patients", label: "Patients", icon: Users },
  { id: "pharmacy", label: "Medicines & Inventory", icon: Pill },
  { id: "pos", label: "POS / Billing", icon: Receipt },
  { id: "printing", label: "Printing", icon: Printer },
  { id: "admin", label: "Administration", icon: ShieldCheck },
  { id: "platform", label: "Platform Console", icon: Building2 },
  { id: "troubleshooting", label: "Troubleshooting", icon: HelpCircle },
];

const articles = [
  {
    id: "login-navigation",
    category: "getting-started",
    title: "Logging in and navigating the system",
    audience: "All users",
    summary: "Use your assigned email and password to access the RX system. The sidebar only shows modules allowed for your role.",
    steps: [
      "Open the RX website.",
      "Enter your registered email and password.",
      "After login, use the left sidebar to open your modules.",
      "Use My Profile to review your account.",
      "Click Logout after using a shared clinic workstation.",
    ],
    related: ["/profile", "/"],
  },
  {
    id: "dashboard-overview",
    category: "getting-started",
    title: "Dashboard overview",
    audience: "Admin, Hospital Admin, Doctor, Staff",
    summary: "The dashboard shows clinic activity, recent prescriptions, and most-prescribed medicines.",
    steps: [
      "Open Dashboard.",
      "Review patient, medicine, and prescription counters.",
      "Use Reprint Last to reopen the latest prescription.",
      "Use New Prescription to start a new RX.",
      "Open recent prescriptions for review or printing.",
    ],
    related: ["/", "/prescriptions", "/prescriptions/new"],
  },
  {
    id: "reception-queue",
    category: "reception",
    title: "Managing the reception queue",
    audience: "Admin, Hospital Admin, Reception",
    summary: "Reception manages walk-in patients, queue entries, doctor assignment, billing status, and patient flow.",
    steps: [
      "Open Reception Queue.",
      "Search for an existing patient first.",
      "Register the patient if no record exists.",
      "Create or update the queue entry.",
      "Assign the patient to the correct doctor.",
      "Monitor the status until consultation or completion.",
    ],
    related: ["/reception", "/queue-display", "/patients"],
  },
  {
    id: "doctor-dashboard",
    category: "doctor",
    title: "Using the doctor dashboard",
    audience: "Admin, Hospital Admin, Doctor",
    summary: "Doctors use this page to view assigned patients and continue consultation work.",
    steps: [
      "Open Doctor Dashboard.",
      "Review today’s assigned patients.",
      "Open the patient record.",
      "Review history, notes, vitals, prescriptions, and attachments.",
      "Proceed to prescription creation when clinically appropriate.",
    ],
    related: ["/doctor-dashboard", "/patients", "/prescriptions/new"],
  },
  {
    id: "new-prescription",
    category: "doctor",
    title: "Creating a new prescription",
    audience: "Admin, Hospital Admin, Doctor",
    summary: "Create prescriptions by selecting a patient, adding clinical details, medicines, and printing the final RX.",
    steps: [
      "Open New Prescription.",
      "Select or confirm the patient.",
      "Enter diagnosis, clinical notes, vitals, and instructions.",
      "Add medicines with dosage, frequency, duration, and instructions.",
      "Save the prescription.",
      "Open preview and print only after checking all details.",
    ],
    warnings: [
      "Do not print until patient name, medicines, and doctor details are correct.",
      "For corrections, edit the prescription instead of creating unnecessary duplicates.",
    ],
    related: ["/prescriptions/new", "/prescriptions"],
  },
  {
    id: "patients",
    category: "patients",
    title: "Managing patients",
    audience: "All hospital users",
    summary: "Patients stores demographic data, contact details, birthday, notes, visit data, labs, and related records.",
    steps: [
      "Open Patients.",
      "Search before adding a new patient.",
      "Click Add Patient for new records.",
      "Complete patient details.",
      "Open Patient Detail to review history and related records.",
    ],
    warnings: ["Always search first to avoid duplicate patient records."],
    related: ["/patients", "/patients/bulk-upload"],
  },
  {
    id: "medicines",
    category: "pharmacy",
    title: "Managing medicines",
    audience: "Admin, Hospital Admin, Doctor, Staff",
    summary: "The Medicines module maintains the medicine list used in prescriptions and pharmacy workflows.",
    steps: [
      "Open Medicines.",
      "Search before adding a new medicine.",
      "Add or edit medicine details.",
      "Use bulk upload for large medicine lists.",
      "Keep medicine names standardized.",
    ],
    related: ["/medicines", "/medicines/bulk-upload"],
  },
  {
    id: "inventory",
    category: "pharmacy",
    title: "Using inventory",
    audience: "Admin, Hospital Admin, Doctor, Staff",
    summary: "Inventory supports medicine or item stock tracking for clinic operations.",
    steps: [
      "Open Inventory.",
      "Review current stock records.",
      "Update stock when items are received, adjusted, or consumed.",
      "Use consistent item naming for clean reporting.",
    ],
    related: ["/inventory", "/medicines"],
  },
  {
    id: "pos-billing",
    category: "pos",
    title: "Using POS / Billing",
    audience: "Admin, Hospital Admin, Doctor, Reception",
    summary: "POS / Billing supports service charging, transactions, and billing review.",
    steps: [
      "Open POS / Billing.",
      "Select the patient or billing context.",
      "Add applicable services or billable items.",
      "Review totals before confirming.",
      "Open transaction details for review or printing.",
    ],
    warnings: ["Confirm patient and amount before finalizing billing."],
    related: ["/pos", "/pos/services", "/pos/reports"],
  },
  {
    id: "print-settings",
    category: "printing",
    title: "Configuring print settings",
    audience: "Admin, Hospital Admin, Doctor",
    summary: "Print Settings controls prescription layout, clinic header, watermark, and print presentation.",
    steps: [
      "Open Print Settings.",
      "Review clinic header and layout options.",
      "Update only confirmed print settings.",
      "Print a test prescription after major layout changes.",
    ],
    warnings: ["Bad print settings can affect every generated prescription."],
    related: ["/settings", "/prescriptions"],
  },
  {
    id: "users",
    category: "admin",
    title: "Managing user accounts",
    audience: "Admin, Hospital Admin",
    summary: "User Accounts controls who can access the hospital workspace and what role each user has.",
    steps: [
      "Open User Accounts.",
      "Create users only for authorized clinic personnel.",
      "Assign the least-privilege role required.",
      "Update or deactivate accounts when staff roles change.",
    ],
    warnings: ["Do not share user accounts. Each clinic user should have a separate login."],
    related: ["/users"],
  },
  {
    id: "referrals",
    category: "admin",
    title: "Using referrals and referral partners",
    audience: "Admin, Hospital Admin, Doctor, Staff",
    summary: "Referrals supports referral documents and external partner records.",
    steps: [
      "Open Referrals to review or create referral records.",
      "Open Referral Partners to maintain external partner details.",
      "Preview referral documents before sharing or printing.",
      "Keep partner details updated.",
    ],
    related: ["/referrals", "/referral-partners"],
  },
  {
    id: "followups",
    category: "admin",
    title: "Managing follow-ups",
    audience: "Admin, Hospital Admin, Doctor, Staff",
    summary: "Follow-ups tracks patients who need future visits or follow-up action.",
    steps: [
      "Open Follow-ups.",
      "Review upcoming or overdue follow-ups.",
      "Open the patient record before action.",
      "Update follow-up status after contact or visit.",
    ],
    related: ["/followups", "/patients"],
  },
  {
    id: "platform-console",
    category: "platform",
    title: "Using the platform console",
    audience: "Platform Admin",
    summary: "The platform console manages hospitals, hospital admins, and doctors across the RX platform.",
    steps: [
      "Log in using a platform admin account.",
      "Open Overview.",
      "Use Hospitals to manage tenant hospital records.",
      "Use Hospital Admins to manage hospital administrator access.",
      "Use Doctors to manage platform-level doctor records.",
    ],
    warnings: ["Platform admin changes can affect multiple hospitals. Verify the target hospital before saving."],
    related: ["/platform", "/platform/hospitals", "/platform/admins", "/platform/doctors"],
  },
  {
    id: "common-issues",
    category: "troubleshooting",
    title: "Common issues and fixes",
    audience: "All users",
    summary: "Use these checks before escalating support issues.",
    steps: [
      "If login fails, confirm email, password, and assigned role.",
      "If a page looks empty, refresh and check the selected hospital.",
      "If a patient is missing, search by partial name or contact number.",
      "If printing looks wrong, check Print Settings and browser print scale.",
      "If access is denied, verify the user role in User Accounts.",
    ],
    related: ["/settings", "/users", "/profile"],
  },
];

const quickLinks = [
  { label: "Register or search patient", to: "/patients", icon: Users },
  { label: "Manage reception queue", to: "/reception", icon: ClipboardList },
  { label: "Doctor dashboard", to: "/doctor-dashboard", icon: Stethoscope },
  { label: "Create prescription", to: "/prescriptions/new", icon: BookOpen },
  { label: "POS / Billing", to: "/pos", icon: Receipt },
  { label: "Print settings", to: "/settings", icon: Printer },
];

function ArticleCard({ article, active, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full text-left rounded-lg border p-4 transition-colors ${
        active ? "border-blue-300 bg-blue-50" : "border-slate-200 bg-white hover:bg-slate-50"
      }`}
      data-testid={`manual-article-${article.id}`}
    >
      <div className="text-sm font-semibold text-slate-900">{article.title}</div>
      <div className="text-xs text-slate-500 mt-1">{article.summary}</div>
      <div className="text-[10px] uppercase tracking-wider text-blue-700 mt-3">{article.audience}</div>
    </button>
  );
}

export default function UserManual() {
  const [query, setQuery] = useState("");
  const [activeCategory, setActiveCategory] = useState("getting-started");
  const [activeId, setActiveId] = useState("login-navigation");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return articles.filter((article) => {
      const matchesCategory = activeCategory === "all" || article.category === activeCategory;
      const haystack = [
        article.title,
        article.audience,
        article.summary,
        ...(article.steps || []),
        ...(article.warnings || []),
      ].join(" ").toLowerCase();

      return matchesCategory && (!q || haystack.includes(q));
    });
  }, [query, activeCategory]);

  const activeArticle = articles.find((a) => a.id === activeId) || filtered[0] || articles[0];

  const openCategory = (id) => {
    setActiveCategory(id);
    const first = articles.find((a) => id === "all" || a.category === id);
    if (first) setActiveId(first.id);
  };

  return (
    <div className="p-6 md:p-8 max-w-7xl" data-testid="user-manual-page">
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 mb-6">
        <div>
          <div className="inline-flex items-center gap-2 rounded-full bg-blue-50 text-blue-700 px-3 py-1 text-xs font-medium mb-3">
            <BookOpen size={14} /> RX Help Center
          </div>
          <h1 className="text-3xl font-semibold text-slate-900 tracking-tight" style={{ fontFamily: "Work Sans" }}>
            User Manual
          </h1>
          <p className="text-slate-500 text-sm mt-1 max-w-2xl">
            Searchable operating guide for reception, doctors, pharmacy/POS, printing, administration, and platform users.
          </p>
        </div>

        <div className="relative w-full lg:w-96">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search help topics..."
            className="pl-9 bg-white"
            data-testid="manual-search"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 mb-6">
        {quickLinks.map((item) => {
          const Icon = item.icon;
          return (
            <Link key={item.to} to={item.to} className="group rounded-lg border border-slate-200 bg-white p-4 hover:bg-slate-50 transition-colors">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-9 h-9 rounded-md bg-blue-50 text-blue-700 flex items-center justify-center shrink-0">
                    <Icon size={16} />
                  </div>
                  <div className="text-sm font-medium text-slate-900 truncate">{item.label}</div>
                </div>
                <ArrowRight size={15} className="text-slate-400 group-hover:text-blue-700" />
              </div>
            </Link>
          );
        })}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-12 gap-6">
        <Card className="xl:col-span-3 p-4 border-slate-200 h-fit">
          <div className="text-xs uppercase tracking-wider text-slate-500 mb-3">Categories</div>
          <div className="space-y-1">
            <button
              type="button"
              onClick={() => openCategory("all")}
              className={`w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm ${
                activeCategory === "all" ? "bg-blue-50 text-blue-700 font-medium" : "text-slate-700 hover:bg-slate-100"
              }`}
            >
              <BookOpen size={15} /> All Articles
            </button>

            {categories.map((category) => {
              const Icon = category.icon;
              return (
                <button
                  key={category.id}
                  type="button"
                  onClick={() => openCategory(category.id)}
                  className={`w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm ${
                    activeCategory === category.id ? "bg-blue-50 text-blue-700 font-medium" : "text-slate-700 hover:bg-slate-100"
                  }`}
                  data-testid={`manual-category-${category.id}`}
                >
                  <Icon size={15} /> {category.label}
                </button>
              );
            })}
          </div>
        </Card>

        <div className="xl:col-span-4 space-y-3">
          <div className="flex items-center justify-between">
            <div className="text-sm font-semibold text-slate-900">Articles</div>
            <div className="text-xs text-slate-500">{filtered.length} result(s)</div>
          </div>

          {filtered.length === 0 ? (
            <Card className="p-6 border-slate-200 text-sm text-slate-500 text-center">
              No help articles matched your search.
            </Card>
          ) : (
            filtered.map((article) => (
              <ArticleCard
                key={article.id}
                article={article}
                active={activeArticle?.id === article.id}
                onClick={() => setActiveId(article.id)}
              />
            ))
          )}
        </div>

        <Card className="xl:col-span-5 p-6 border-slate-200 h-fit" data-testid="manual-article-viewer">
          <div className="text-xs uppercase tracking-wider text-blue-700 mb-2">{activeArticle.audience}</div>
          <h2 className="text-2xl font-semibold text-slate-900 tracking-tight" style={{ fontFamily: "Work Sans" }}>
            {activeArticle.title}
          </h2>
          <p className="text-sm text-slate-600 mt-2 mb-5">{activeArticle.summary}</p>

          <div className="border-t border-slate-100 pt-5">
            <h3 className="text-sm font-semibold text-slate-900 mb-3">Steps</h3>
            <ol className="space-y-3">
              {(activeArticle.steps || []).map((step, index) => (
                <li key={index} className="flex gap-3 text-sm text-slate-700">
                  <span className="w-6 h-6 rounded-full bg-blue-50 text-blue-700 flex items-center justify-center text-xs font-semibold shrink-0">
                    {index + 1}
                  </span>
                  <span>{step}</span>
                </li>
              ))}
            </ol>
          </div>

          {activeArticle.warnings?.length ? (
            <div className="mt-6 rounded-lg border border-amber-200 bg-amber-50 p-4">
              <h3 className="text-sm font-semibold text-amber-900 mb-2">Important reminders</h3>
              <ul className="space-y-2">
                {activeArticle.warnings.map((warning, index) => (
                  <li key={index} className="flex gap-2 text-sm text-amber-800">
                    <CheckCircle2 size={15} className="mt-0.5 shrink-0" /> {warning}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          {activeArticle.related?.length ? (
            <div className="mt-6 border-t border-slate-100 pt-5">
              <h3 className="text-sm font-semibold text-slate-900 mb-3">Related pages</h3>
              <div className="flex flex-wrap gap-2">
                {activeArticle.related.map((to) => (
                  <Button key={to} asChild variant="outline" size="sm">
                    <Link to={to}>{to}</Link>
                  </Button>
                ))}
              </div>
            </div>
          ) : null}
        </Card>
      </div>
    </div>
  );
}
